import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiClientError } from '@/lib/api-client';
import {
  attachSourceFileFromLibrary,
  listSourceFiles,
  uploadSourceFile,
  type AttachSourceFileFromLibraryInput,
  type UploadSourceFileInput,
} from './api';
import type { SourceFileDto } from './types';

const KEY = 'source-files' as const;

export const sourceFilesKey = (batchId: string) => [KEY, batchId] as const;

export const useSourceFiles = (batchId: string | undefined) => {
  return useQuery<SourceFileDto[], ApiClientError>({
    queryKey: [KEY, batchId ?? ''],
    queryFn: () => listSourceFiles(batchId as string),
    enabled: !!batchId,
  });
};

export const useUploadSourceFile = (batchId: string | undefined) => {
  const qc = useQueryClient();
  return useMutation<SourceFileDto, ApiClientError, Omit<UploadSourceFileInput, 'batchId'>>({
    mutationFn: (input) => uploadSourceFile({ ...input, batchId: batchId as string }),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, batchId ?? ''] }),
  });
};

export const useAttachSourceFileFromLibrary = (batchId: string | undefined) => {
  const qc = useQueryClient();
  return useMutation<
    SourceFileDto,
    ApiClientError,
    Omit<AttachSourceFileFromLibraryInput, 'batchId'>
  >({
    mutationFn: (input) => attachSourceFileFromLibrary({ ...input, batchId: batchId as string }),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, batchId ?? ''] }),
  });
};
